import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { APP_USER_QUERY_KEY } from "./app-user.query.key";

const ITEMS_PER_PAGE = 20;

export function useInfiniteEmployees(searchQuery?: string) {
  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: [APP_USER_QUERY_KEY, "infinite", searchQuery],
      queryFn: async (args) => {
        const res = await api.appUser.getAll({
          filter: {
            isArchived: { operator: "equals", value: false },
            ...(searchQuery
              ? {
                  "user.name": { operator: "contains", value: searchQuery },
                }
              : undefined),
          },
          sort: { field: "user.name", order: "asc" },
          include: {
            user: { select: true },
          },
          includeSignedUrls: true,
          pagination: {
            take: ITEMS_PER_PAGE,
            page: args.pageParam + 1,
          },
        });
        return res.data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const totalItems = lastPage.totalAmount;
        const currentItems = (lastPageParam + 1) * ITEMS_PER_PAGE;

        if (currentItems >= totalItems) {
          return undefined;
        }

        return lastPageParam + 1;
      },
    });

  const employees = data?.pages.flatMap((page) => page.items ?? []) ?? [];

  const loadMore = async (ev: CustomEvent) => {
    await fetchNextPage();
    if (ev.target) {
      (ev.target as HTMLIonInfiniteScrollElement).complete();
    }
  };

  return {
    employees,
    loadMore,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
  };
}
